import { DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { Moment } from 'moment';
import { StandByModes } from './models/enums/stand-by-modes';
import { SetInStandByStateData } from './models/set-in-stand-by-state-data';

@Component({
    selector: 'app-set-in-stand-by-state',
    templateUrl: './set-in-stand-by-state.component.html',
    styleUrls: ['./set-in-stand-by-state.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        MatOptionModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
        MatDatepickerModule,
        MatButtonModule,
    ],
})
export class SetInStandByStateComponent {
    public StandByModes = StandByModes;

    /**
     * Champs du formulaire de saisie de la durée.
     */
    public durationFormControl: UntypedFormControl;

    public endDateFormControl: UntypedFormControl;

    public standByMode: StandByModes = StandByModes.Duration;

    public isValid: boolean = false;

    private partRegex = new RegExp(/^(?<count>\d+)(?<unit>[d,j,h,m]{1})$/);

    private durationRegex = new RegExp(/^(\d+[d,j]{1})?\s*(\d+[h]{1})?\s*(\d+[m]{1})?$/);

    /**
     * Constructeur.
     * @param dialogRef
     * @param data
     */
    constructor(
        public dialogRef: MatDialogRef<SetInStandByStateComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SetInStandByStateData,
        public datePipe: DatePipe
    ) {
        this.durationFormControl = new UntypedFormControl(null, [Validators.required, Validators.pattern(this.durationRegex)]);
        this.endDateFormControl = new UntypedFormControl(null, [Validators.required]);
        this.endDateFormControl.disable();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public validate(): void {
        if (this.isValid) {
            this.data.duration = this.getStandByDuration();
            this.dialogRef.close(this.data);
        }
    }

    public standByModeChange(mode: StandByModes) {
        this.standByMode = mode;
        this.isValid = false;

        if (this.standByMode === StandByModes.Duration) {
            this.durationFormControl.enable();
            this.endDateFormControl.disable();
            this.endDateFormControl.setValue(null);
        } else {
            this.durationFormControl.disable();
            this.durationFormControl.setValue(null);
            this.endDateFormControl.enable();
        }
    }

    /**
     * Emis lorsque la date est modifiée (perte de focus ou selection dans le calendrier)
     * @param $event Événement.
     */
    public onDateChange($event): void {
        let m: Moment = $event.value;

        if (m) {
            this.data.endDate = m.toDate();
            this.isValid = true;
        } else {
            this.isValid = false;
            this.data.endDate = null;
        }
    }

    /**
     * Emis lorsque la durée est modifiée (perte de focus)
     * @param $event Événement.
     */
    public onDurationChange($event): void {
        if (this.IsStandByDurationValid() === true) {
            this.isValid = true;
        } else {
            this.isValid = false;
        }
    }

    /**
     * Retourne la durée de mise en veille en minutes
     */
    private getStandByDuration(): number {
        const durationTextRepresentation: string = this.durationFormControl.value;

        if (!durationTextRepresentation) {
            return null;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        let duration = 0;

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                let result = this.partRegex.exec(part.trim());
                const count = result.groups['count'];
                const unit = result.groups['unit'];

                switch (unit) {
                    case 'd':
                    case 'j':
                        duration += +count * (60 * 24);
                        break;
                    case 'h':
                        duration += +count * 60;
                        break;
                    case 'm':
                        duration += +count;
                        break;
                }
            });
        }

        return duration;
    }

    /**
     * Retourne une valeur indiquant si la durée de mise en veille est valide.
     */
    private IsStandByDurationValid(): boolean {
        let isValid = true;

        const durationTextRepresentation: string = this.durationFormControl.value;

        if (!durationTextRepresentation) {
            return false;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                if (this.partRegex.test(part.trim()) === false) {
                    isValid = false;
                }
            });
        } else {
            isValid = false;
        }

        return isValid;
    }
}
